import { useCallback, useEffect, useState } from 'react';

// Połączenie domu z Lidl Plus (trasy /api/lidl/*). Wymaga internetu — paragony i tak przychodzą z serwera.

export type LidlConnection = 'none' | 'pending' | 'connected' | 'expired';

export interface LidlStatus {
  status: LidlConnection;
  connectedBy: string | null;
  connectedAt: number | null;
  lastSyncAt: number | null;
  lastOkAt: number | null;
  lastError: string | null;
  historyAt: number | null;
  running: boolean;
  progress: { done: number; total: number } | null;
  receipts: number;
  couponsMode: CouponsMode;
  couponsModeAt: number | null;
  couponLog: { ms: number; title: string; discount: string; status: 'would' | 'activated' | 'failed' }[];
  promoCheckedAt: number | null;
}

/** Kupony Lidl Plus: wyłączone, tryb próbny (tylko zapis decyzji), aktywacja. */
export type CouponsMode = 'off' | 'dry' | 'on';

export class LidlError extends Error {
  constructor(readonly code: string) {
    super(LIDL_MESSAGES[code] ?? 'Coś poszło nie tak. Spróbuj ponownie.');
  }
}

const LIDL_MESSAGES: Record<string, string> = {
  offline: 'Brak połączenia z internetem.',
  no_login: 'Najpierw otwórz stronę logowania Lidla (przycisk powyżej) — logowanie ważne jest 30 minut.',
  bad_code: 'To nie jest adres z logowania. Skopiuj cały nagłówek Location zaczynający się od com.lidlplus.app://callback.',
  state_mismatch: 'Ten adres pochodzi z innego logowania. Otwórz stronę logowania jeszcze raz.',
  rejected: 'Lidl odrzucił kod — jest jednorazowy i ważny kilka minut. Zaloguj się jeszcze raz.',
  failed: 'Lidl nie odpowiada. Spróbuj za chwilę.',
  not_found: 'Nie ma takiej listy albo paragonu.',
};

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!navigator.onLine) throw new LidlError('offline');
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new LidlError('offline');
  }
  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok) throw new LidlError(data?.error ?? 'failed');
  return data as T;
}

export const getLidlStatus = () => request<LidlStatus>('GET', '/api/lidl');
export const startLidlConnect = () => request<{ authUrl: string }>('POST', '/api/lidl/connect').then((r) => r.authUrl);
export const finishLidlConnect = (redirect: string) => request<{ ok: true }>('POST', '/api/lidl/connect/finish', { redirect });
export const disconnectLidl = () => request<{ ok: true }>('DELETE', '/api/lidl');
export const setCouponsMode = (couponsMode: CouponsMode) => request<LidlStatus>('PATCH', '/api/lidl', { couponsMode });

/** Pobierz paragony teraz; 'finished' = koniec zakupów w Lidlu (serwer przez chwilę sprawdza częściej). Bez błędów. */
export async function requestLidlSync(reason: 'manual' | 'finished' = 'manual'): Promise<void> {
  await request('POST', '/api/lidl/sync', { reason }).catch(() => {});
}

/** Użytkownik wskazał, do której listy należy paragon. */
export const chooseReceiptList = (receiptId: string, listId: string) =>
  request<{ ok: true; auto: number; ai: number; suggested: number }>('POST', `/api/lidl/receipts/${encodeURIComponent(receiptId)}/list`, { listId });

/** Stan połączenia z odświeżaniem co kilka sekund, gdy trwa pobieranie. undefined = wczytuję, null = błąd. */
export function useLidlStatus(): { status: LidlStatus | null | undefined; refresh: () => Promise<void> } {
  const [status, setStatus] = useState<LidlStatus | null | undefined>(undefined);
  const refresh = useCallback(async () => {
    try {
      setStatus(await getLidlStatus());
    } catch {
      setStatus(null);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const busy = !!status && (status.running || status.status === 'pending');
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => void refresh(), status?.running ? 3000 : 10_000);
    return () => clearInterval(t);
  }, [busy, status?.running, refresh]);
  return { status, refresh };
}
